import React from 'react';
import styled from 'astroturf/react';

import type { StatLayout, StatStyle } from './arms';
import { formatCount } from './formatCount';

export const STACKED_TILE_WIDTH = 68;
export const INLINE_TILE_WIDTH = 140;

const TileRoot = styled.a<{ layout: StatLayout }>`
  @import 'stylesheets/design_system/font';
  @include sans-serif;

  display: flex;
  box-sizing: border-box;
  align-items: center;
  color: var(--text-primary, #141413);
  text-decoration: none;

  &:hover,
  &:visited {
    color: var(--text-primary, #141413);
    text-decoration: none;
  }

  /* The anchor fills its cell so the whole tile region is clickable; the
     content wrapper below keeps the rendered pixels where they were. Width
     hugs the content — only skeletons reserve space (SKELETON_WIDTHS). */
  align-self: stretch;

  &.layout-stacked {
    flex: 0 0 auto;
  }

  &.layout-inline {
    flex: 1 1 auto;
  }
`;

const TileContent = styled.span<{ statStyle?: StatStyle }>`
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;

  &.statStyle-delta_chip {
    align-items: flex-end;
    column-gap: 14px;
  }

  &.statStyle-trend_icon {
    column-gap: 8px;
  }
`;

const StatGroup = styled.span<{ layout: StatLayout; statStyle: StatStyle }>`
  display: flex;
  min-width: 0;

  &.layout-stacked {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 6px;
  }

  &.layout-stacked.statStyle-delta_chip {
    align-items: flex-start;
    row-gap: 3px;
  }

  &.layout-inline {
    position: relative;
    flex-direction: row;
    align-items: baseline;
    column-gap: 6px;
  }
`;

const CountRow = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
`;

const Count = styled.span`
  @import 'stylesheets/design_system/font';
  @include mono;

  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;

const DotAnchor = styled.span<{ anchor: 'number' | 'row_end' }>`
  position: absolute;
  width: 8px;
  height: 8px;
  margin-left: 4px;
  border-radius: 50%;
  background: var(--error-dark, #b60000);

  &.anchor-number {
    top: -2.5px;
    right: -9px;
  }

  &.anchor-row_end {
    top: -2px;
    right: -8px;
  }
`;

const AdornmentSlot = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
`;

const Label = styled.span<{ layout: StatLayout; statStyle: StatStyle }>`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  color: var(--text-primary, #141413);
  white-space: nowrap;
  text-overflow: ellipsis;

  &.layout-stacked {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  &.layout-stacked.statStyle-plain {
    font-size: 13px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.13px;
  }

  &.layout-inline {
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.14px;
  }
`;

export type ImpactStatTileProps = {
  count: number | null;
  label: string;
  layout: StatLayout;
  statStyle?: StatStyle;
  href: string;
  adornment?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  showDot?: boolean;
  className?: string;
};

const ImpactStatTile = ({
  count,
  label,
  layout,
  statStyle = 'plain',
  adornment,
  href,
  onClick,
  showDot = true,
  className,
}: ImpactStatTileProps) => {
  if (count == null) {
    return null;
  }

  const leadingAdornment = statStyle === 'trend_icon';
  const slot = adornment != null && (
    <AdornmentSlot data-testid="impact-stat-adornment">
      {adornment}
    </AdornmentSlot>
  );

  const dotAnchor: 'number' | 'row_end' =
    layout === 'inline' ? 'row_end' : 'number';
  const dot = showDot && (
    <DotAnchor anchor={dotAnchor} data-testid="impact-stat-dot" />
  );

  return (
    <TileRoot
      data-testid="impact-stat-tile"
      layout={layout}
      href={href}
      onClick={onClick}
      className={className}
    >
      <TileContent statStyle={statStyle === 'plain' ? undefined : statStyle}>
        {leadingAdornment && slot}
        <StatGroup layout={layout} statStyle={statStyle}>
          <CountRow>
            <Count data-testid="impact-stat-count">{formatCount(count)}</Count>
            {dotAnchor === 'number' && dot}
          </CountRow>
          <Label layout={layout} statStyle={statStyle}>
            {label}
          </Label>
          {dotAnchor === 'row_end' && dot}
        </StatGroup>
        {!leadingAdornment && slot}
      </TileContent>
    </TileRoot>
  );
};

export default ImpactStatTile;
